import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Col, Row } from 'reactstrap';

import globalTranslations from '../../../../i18n/globalTranslations';
import { createForm } from '../../../../models/Form';
import { createFormField } from '../../../../models/FormField';
import { modelOf } from '../../../../prop-types';
import CheckoutStore from '../../../../store/CheckoutStore';
import FormFieldId from '../../../../types/FormFieldId';
import { getReferenceFieldValidators } from '../../../../util/checkoutFormUtils';
import { labels } from '../../../../util/formLabels';
import safeCloneDeep from '../../../../util/safeCloneDeep';
import FormGroupField from '../../../form/FormGroupField';

const CheckoutYourReferenceForm = ({ checkoutStore, onOrderUpdate }) => {
  const { cancelOrder, order } = checkoutStore;
  const yourReferenceFormRef = useRef(null);

  // Initialize form once on mount
  if (!yourReferenceFormRef.current) {
    // Try customer_fields first, fallback to shipping_customer_fields
    let yourReferenceField = order.getCustomerField(
      FormFieldId.YOUR_REFERENCE
    ).field;

    const yourReferenceFormFields = {};
    if (yourReferenceField) {
      const yourReferenceValidators = getReferenceFieldValidators(
        yourReferenceField,
        yourReferenceField.name || labels.reference
      );

      yourReferenceFormFields[FormFieldId.YOUR_REFERENCE] = createFormField(
        safeCloneDeep(yourReferenceField),
        yourReferenceValidators
      );
    }

    yourReferenceFormRef.current = createForm(yourReferenceFormFields);
  }

  // Sync field value when order changes
  useEffect(() => {
    if (!yourReferenceFormRef.current) return;

    const formField = yourReferenceFormRef.current.getField(
      FormFieldId.YOUR_REFERENCE
    );
    if (!formField) return;

    // Get the current value from the order
    let currentValue = order.getCustomerField(FormFieldId.YOUR_REFERENCE).field
      ?.value;

    // Only update if the value has changed to avoid unnecessary updates
    if (formField.value !== currentValue) {
      formField.setValue(currentValue || '');
    }
  }, [order.getCustomerField(FormFieldId.YOUR_REFERENCE).field?.value, order]);

  const yourReferenceForm = yourReferenceFormRef.current;

  if (!yourReferenceForm) {
    return null;
  }

  const yourReferenceField = yourReferenceForm.getField(
    FormFieldId.YOUR_REFERENCE
  );

  if (!yourReferenceField) {
    return null;
  }

  let yourReferenceCustomerField = order.getCustomerField(
    FormFieldId.YOUR_REFERENCE
  ).field;

  if (!yourReferenceCustomerField) {
    yourReferenceCustomerField = order.getShippingCustomerField(
      FormFieldId.YOUR_REFERENCE
    ).field;
  }

  const handleChange = (e) => {
    cancelOrder();
    yourReferenceCustomerField.setValue(e.target.value);
  };

  // onBlur handler receives (event, field) but we only need the field
  const handleBlur = (_, field) => {
    yourReferenceForm.validateField(field.id);

    // User has no input, so no update is needed
    if (
      (field.value == null || field.value === '') &&
      field.value === field.previousValue
    ) {
      return;
    }
    // User has no input and the previousValue is uninitialized
    if (field.value !== field.previousValue && field.previousValue == null) {
      field.updatePreviousValue();
      return;
    }

    field.updatePreviousValue();
    onOrderUpdate({ withoutTimer: true, fetchPickupPoints: false });
  };

  const type = yourReferenceField.type
    ? yourReferenceField.type.toLowerCase()
    : 'text';

  return (
    <div className="CheckoutYourReferenceForm">
      <Row>
        <Col xs={12}>
          <FormGroupField
            field={yourReferenceField}
            fieldName={FormFieldId.YOUR_REFERENCE}
            formName={'checkout'}
            floatingLabel
            label={
              yourReferenceCustomerField.name || (
                <FormattedMessage {...globalTranslations.yourReferenceTitle} />
              )
            }
            required={yourReferenceField.required}
            type={type}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={cancelOrder}
          />
        </Col>
      </Row>
    </div>
  );
};

CheckoutYourReferenceForm.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
};

export default inject('checkoutStore')(observer(CheckoutYourReferenceForm));
